/**
 * ProductTourDialog
 *
 * A modal dialog that guides users through a multi-step product tour.
 * Each step displays a title, optional image, and descriptive content.
 * Users can navigate with Previous/Next buttons, skip the tour, or choose
 * never to show the tour again.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import step1 from './../assets/images/step-1.png';
import step2 from './../assets/images/step-2.png';
import step3 from './../assets/images/step-3.png';
import step4 from './../assets/images/step-4.png';
import step5 from './../assets/images/step-5.png';
import step6 from './../assets/images/step-6.png';

// Define the sequence of steps: their titles, content, and optional images
const steps = [
  {
    title: "Welcome to the Demo!",
    content: (
      <>
        Let's take a quick tour to understand how to use this application.
      </>
    ),
    image: null,
  },
  {
    title: "Step 1: Explore the Map",
    content: "On the left, you'll see an interactive map. Notice that nearby markers are grouped into clusters to keep the map clean.",
    image: step1,
  },
  {
    title: "Step 2: Zoom to See Individual Markers",
    content: "As you zoom in on the map, the clusters will automatically expand, revealing the individual markers at each specific location. Click on any marker to select it.",
    image: step2,
  },
  {
    title: "Step 3: View Information",
    content: "Once you select a marker, detailed information about that location will appear in the panel below the map.",
    image: step3,
  },
  {
    title: "Step 4: See the Image",
    content: "On the right side, the corresponding image for the selected location will be displayed.",
    image: step4,
  },
  {
    title: "Step 5: Inferred Features",
    content: "You can toggle the 'Show Inferred Features' switch to overlay detected objects on the image.",
    image: step5,
  },
  {
    title: "You're all set!",
    content: "Feel free to explore the map and the data. Use the 'Previous' and 'Next' buttons below the image to navigate between selected points.",
    image: step6,
  },
];

function ProductTourDialog({ open, onClose, onDontShowAgain }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentImageSrc, setCurrentImageSrc] = useState(steps[0]?.image || null);

  // Update image source and loading state whenever the step changes
  useEffect(() => {
    setCurrentImageSrc(steps[currentStep]?.image || null);
    setImageLoading(true);

    const img = new Image();
    img.onload = () => {
      setImageLoading(false);
    };
    img.onerror = () => {
      setImageLoading(false);
      console.error("Error loading image for step:", currentStep, steps[currentStep]?.image);
    };
    if (steps[currentStep]?.image) {
      img.src = steps[currentStep].image;
    } else {
      setImageLoading(false);
    }
  }, [currentStep]);

  const handleNext = () => {
    setCurrentStep((prevStep) => prevStep + 1);
  };

  const handlePrevious = () => {
    setCurrentStep((prevStep) => prevStep - 1);
  };

  const handleClose = () => {
    onClose(dontShowAgain);
  };

  const handleDontShowAgainChange = (event) => {
    setDontShowAgain(event.target.checked);
    if (currentStep === 0 && event.target.checked) {
      onDontShowAgain(true);
      onClose(true);
    }
  };

  const currentStepContent = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="product-tour-dialog">
      <DialogTitle id="product-tour-dialog">{currentStepContent.title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          {currentImageSrc ? (
            imageLoading ? (
              <CircularProgress />
            ) : (
              <img
                key={`step-image-${currentStep}`}
                src={currentImageSrc}
                alt={`Step ${currentStep + 1}`}
                style={{ maxWidth: '100%', maxHeight: '300px' }}
              />
            )
          ) : null}
        </Box>
        <Typography>
          {isFirstStep ? (
            <>
              {steps[0].content}
              <FormControlLabel
                control={<Checkbox checked={dontShowAgain} onChange={handleDontShowAgainChange} color="primary" />}
                label="Don't show this tour again"
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            currentStepContent.content
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        {!isFirstStep && <Button onClick={handlePrevious} disabled={imageLoading}>Previous</Button>}
        <Button onClick={handleClose} disabled={imageLoading}>
          {isLastStep ? 'Close' : 'Skip Tour'}
        </Button>
        {!isLastStep && <Button onClick={handleNext} autoFocus disabled={imageLoading}>Next</Button>}
      </DialogActions>
    </Dialog>
  );
}

export default ProductTourDialog;